import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, NavLink, Navigate, useNavigate, useLocation } from 'react-router-dom';
import * as signalR from '@microsoft/signalr';
import { ReactComponent as Logo } from '../../assets/svg/LogoHeader.svg';
import { useBranding } from '../../context/brandingContext';

import {
  NavMenuContainer,
  NavMenuCon,
  NavMenuMobileContainer,
  ProfileDropdownContainer,
  ProfileDrop,
  ProfileDropdownMenu,
  DropDownMenuSuperAdmin,
  ProfileDropWrapper,
  HeaderNotice,
  NetworkNotice,
  NavbarContainerMain,
  MobileNavItem,
  DropDownMenuAdmin,
  Spacer,
  MobileNavOverlay,
} from './navbar.styled';
import AuthService from '../../services/authService';
import { useDispatch, useSelector } from 'react-redux';
import { selectAuthRoutesList } from '../../pages/configuration/routeSlice';
import Heading from '../typography';
import Notification from './notification';
import {
  useBoolean,
  // useGetAwardParticipantDashboardDetails,
  useGetCurrentTenantInfo,
  useGetMyHoldingOverview,
  useGetParticipantOnboardingStatus,
  useGetParticipantTradePreClearances,
  useGetSignUpInformation,
  useGetUnseenNotificationCount,
  usePostCheckPershingSsoEligibility,
  useWealthlaneAwardApi,
  useWealthlaneCustomerApi,
  useWealthlaneIdentityApi,
  useWealthlaneNotificationApi,
} from '../../hooks';
import AnimationWrapper from '../animation/animationWrapper';
import NotificationModal from './notification/notificationModal';
import Button from '../button';
// eslint-disable-next-line camelcase
import { type Wealthlane_NotificationService_DTOs_BellNotificationDto } from '../../services/wealthlane-notification-services';
import { selectToken } from '../../auth/authSlice';
import { setNotify } from '../../auth/notify';
import { TransactionsList } from '../../constants/innerRoutesList';
// import { Chatbot } from '@wealthlane/chatbot';
import { NavLogo } from './navLogo';
import {
  selectEsppParticipantId,
  selectParticipantId,
  selectTenantId,
} from '../../pages/configuration/participantSignupSlice';
import clsx from 'clsx';
import { axiosInstance } from '../../services/apiInterceptor';
import ChatbotOverlay from '../chatbot/ChatbotOverlay';
import { PershingSsoService } from '../../services/wealthlane-identity-services/services/PershingSsoService';
import AlertPopup from '../alert-popup';
import useOnline from '../../hooks/custom/useOnline';

interface NavbarType {
  isAuth: boolean;
  showNotify: boolean;
  isNotify: boolean;
}

const Navbar = ({ isAuth, showNotify, isNotify }: NavbarType): JSX.Element => {
  const skipped = localStorage.getItem('isSkipped');
  const dispatch = useDispatch();
  const authService = new AuthService();
  const token = useSelector(selectToken);
  const id = useSelector(selectTenantId);
  const participantId = useSelector(selectParticipantId);
  const esppParticipantId = useSelector(selectEsppParticipantId);
  const isEsppOnly = participantId === null && esppParticipantId !== null;
  const userAuthList = useSelector(selectAuthRoutesList);
  const { handleRequest: info } = useWealthlaneCustomerApi();
  const { data: signUpInfo, refetch: signUpInfoRefetch } = useGetSignUpInformation(info, token);
  // const onboarding = signUpInfo?.isOnBoarded;
  const { data: onboardingStatus } = useGetParticipantOnboardingStatus(info, token);
  const {
    appLogoUrl,
    isEnabled: isBrandingEnabled,
    isResolved: isBrandingResolved,
  } = useBranding();

  // const { data: personalInfo } = useGetParticipantPersonalDetails(info, onboarding);
  const [isLoggedIn, setLoggedIn] = useState(true);
  const { handleRequest: NotificationHandleRequest } = useWealthlaneNotificationApi();
  const { value: isModalOpen, setFalse: closeModal, setTrue: openModal } = useBoolean(false);
  const { data: NotificationCount, refetch } = useGetUnseenNotificationCount(
    NotificationHandleRequest,
    token
  );
  const [toggleTransactions, setToggleTransactions] = useState(false);

  const [openSideNav, setOpenSideNav] = useState(false);
  const [open, setOpen] = useState(false);
  const [newMessage, setNewMessage] =
    // eslint-disable-next-line camelcase
    useState<Wealthlane_NotificationService_DTOs_BellNotificationDto>({});
  const isMobile = localStorage.getItem('isMobile') === 'true';
  const { isOffline } = useOnline();

  const onSideNavClick = (isClose?: boolean): void => {
    setOpenSideNav(!openSideNav);
    if (isClose ?? false) {
      document.getElementsByTagName('html')[0].style.overflow = 'unset';
    } else {
      document.getElementsByTagName('html')[0].style.overflow = 'hidden';
    }
  };
  const { handleRequest } = useWealthlaneCustomerApi();
  const { handleRequest: identityHandleRequest } = useWealthlaneIdentityApi();
  // const { data } = useGetSignUpInformation(handleRequest, token);

  const navigate = useNavigate();
  const location = useLocation();

  // Check if we're in an authentication flow where branding is not available
  const isAuthenticatingFlow = useMemo(() => {
    const pathname = location.pathname.toLowerCase();
    const search = location.search.toLowerCase();

    // Callback routes, impersonation, or any auth-related loading states
    return (
      pathname.includes('/callback') ||
      pathname.includes('/silent-renew') ||
      pathname.includes('/signin') ||
      pathname.includes('/login') ||
      search.includes('impersonatetoken') ||
      search.includes('code=') // OAuth callback with code
    );
  }, [location.pathname, location.search]);

  const wrapperRef: any = useRef(null);
  useEffect(() => {
    function handleClickOutside(event: Event): void {
      if (
        Boolean(wrapperRef.current) &&
        !(wrapperRef.current as HTMLElement).contains(event.target as Node)
      ) {
        setOpenSideNav(false);
        setOpen(false);
        setToggleTransactions(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [wrapperRef]);

  const [isPershingEligible, setIsPershingEligible] = useState(false);
  const [pershingRedirectUrl, setPershingRedirectUrl] = useState<string | null>(null);
  const { mutate: checkPershingSsoEligibility } = usePostCheckPershingSsoEligibility(
    identityHandleRequest,
    (data) => {
      setIsPershingEligible(data?.isEligible ?? false);
    }
  );
  useEffect(() => {
    if (participantId) {
      checkPershingSsoEligibility(participantId);
    }
  }, [participantId]);

  useEffect(() => {
    if (showNotify) {
      document.body.style.paddingTop = '67px';
    } else {
      document.body.style.paddingTop = '67px';
    }
  }, [showNotify]);

  const storageEventHandler = (): void => {
    dispatch(setNotify({ notify: localStorage.getItem('Notify') === 'Yes' }));
  };

  useEffect(() => {
    dispatch(setNotify({ notify: localStorage.getItem('Notify') === 'Yes' }));
    window.addEventListener('storage', storageEventHandler, false);
  }, []);

  const handleRemoveNotify = (): void => {
    localStorage.setItem('Notify', 'No');
    dispatch(setNotify({ notify: false }));
  };

  useEffect(() => {
    if (isModalOpen) setLoggedIn(false);
  }, [isModalOpen]);

  // useEffect(() => {
  //   if (isLoggedIn && isNotify) {
  //     const connection = new signalR.HubConnectionBuilder()
  //       .withUrl(
  //         // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
  //         `${process.env.REACT_APP_WEALTHLANE_API_ENDPOINT}/api/notification/signalr-hubs/bellNotification`,
  //         {
  //           transport: signalR.HttpTransportType.LongPolling,
  //           accessTokenFactory: () => token ?? '',
  //         }
  //       )
  //       .configureLogging(signalR.LogLevel.Information)
  //       .build();

  //     const start = async (): Promise<void> => {
  //       try {
  //         await connection.start();
  //       } catch (err) {
  //         setTimeout(() => {
  //           void start();
  //         }, 5000);
  //       }
  //     };

  //     connection.onclose(() => {
  //       void start();
  //     });

  //     connection.on('ReceiveBellNotification', (user, message) => {
  //       void refetch();
  //       setNewMessage(message);
  //     });

  //     // Start the connection.
  //     void start();
  //   }
  // }, [isLoggedIn]);
  // if (data?.isOnBoarded === false) {
  //   return <Navigate to={`/onboarding`} />;
  // }

  const {
    value: isChatModalOpen,
    setFalse: closeChatModal,
    setTrue: openChatModal,
  } = useBoolean(false);

  const {
    value: isPershingRedirectOpen,
    setFalse: closePershingRedirectModal,
    setTrue: openPershingRedirectModal,
  } = useBoolean(false);

  useEffect(() => {
    signUpInfoRefetch();
  }, [id !== null]);

  const { handleRequest: customerHandleRequest } = useWealthlaneCustomerApi();
  const { handleRequest: handleRequestAward } = useWealthlaneAwardApi();

  const isPptView = userAuthList?.extraProperties?.[`ImpersonationInfo:ParticipantId`];

  // const { data: myHoldingOverviewDetails, isLoading } = useGetMyHoldingOverview(
  //   handleRequestAward,
  //   onboarding && Boolean(id)
  // );
  const { data: currentTenantInfo, isLoading: currentTenantInfoLoading } = useGetCurrentTenantInfo(
    customerHandleRequest,
    Boolean(isPptView)
  );
  const adminName =
    userAuthList?.extraProperties &&
    userAuthList?.extraProperties['ImpersonationInfo:UserDisplayName'];
  // const hasReadOnlyImpersonatePermission =
  //   userAuthList?.auth?.grantedPolicies['wealthlane_is_readonly_session_policy'];
  // const hasWriteAccessImpersonatePermission =
  //   userAuthList?.auth?.grantedPolicies['wealthlane_write_access_policy'];

  // const {
  //   data: awardParticipantDashboardDetails,
  //   isLoading: awardParticipantDashboardDetailsLoading,
  // } = useGetAwardParticipantDashboardDetails(handleRequestAward);
  const { data: participantTradePreClearances, isLoading: participantTradePreClearancesLoading } =
    useGetParticipantTradePreClearances(handleRequestAward, signUpInfo?.isOnBoarded, isEsppOnly);
  if (onboardingStatus?.requiresPartB && !location.pathname?.includes('onboarding')) {
    navigate('/onboarding');
  }

  const handlePershingDocument = (id: string): void => {
    identityHandleRequest(
      PershingSsoService.postApiIdentityApiAppPershingSsoGenerateTokenForParticipant(id)
    )
      .then((data: any) => {
        if (data && data?.data?.url) {
          setPershingRedirectUrl(data?.data?.url);

          openPershingRedirectModal();
        }
      })
      .catch((error: any) => {
        console.log('error', error);
      });
  };

  return (
    <>
      {/* {showNotify && (
        <HeaderNotice>
          <Heading
            variant='body-s'
            title={
              <span className='flex gap-x-2 '>
                Need help? Contact us at
                <a href='mailto:support@wealthlane.co' className='text-blue-600 underline'>
                  support@wealthlane.co
                </a>
              </span>
            }
          />
          <span
            className='material-symbols-outlined closeIcon'
            onClick={() => {
              handleRemoveNotify();
            }}>
            close
          </span>
        </HeaderNotice>
      )} */}
      {!token && !id && (
        <NavbarContainerMain hasNotify={showNotify} isAdminView={isPptView} className='nav'>
          {/* Don't show any logo during authentication flows (callback, impersonation, etc.) 
              because we can't fetch branding config without proper auth context.
              When branding is not enabled (no auth), show fallback Wealthlane logo.
              When branding is enabled, wait for it to resolve before showing logo. */}
          {!isAuthenticatingFlow && (
            <Link to='/'>
              {isBrandingEnabled ? (
                // Branding is enabled - wait for it to resolve, then show branded or fallback logo
                isBrandingResolved &&
                (appLogoUrl ? (
                  <img
                    src={appLogoUrl}
                    alt='Logo'
                    className='nav-brand'
                    style={{ height: '40px', objectFit: 'contain' }}
                  />
                ) : (
                  <Logo className='nav-brand' />
                ))
              ) : (
                // Branding not enabled (unauthenticated pages like sign-up) - show fallback Wealthlane logo
                <Logo className='nav-brand' />
              )}
            </Link>
          )}
        </NavbarContainerMain>
      )}
      {token && id && !signUpInfo?.taxCertificationFilled && !isPptView && !esppParticipantId && (
        <NavbarContainerMain hasNotify={showNotify} isAdminView={isPptView} className='nav'>
          {/* Only show logo after branding config has resolved */}
          {isBrandingResolved &&
            (appLogoUrl ? (
              <img
                src={appLogoUrl}
                alt='Logo'
                className='nav-brand'
                style={{ height: '40px', objectFit: 'contain' }}
              />
            ) : (
              <Logo className='nav-brand' />
            ))}

          <div className='logout flex gap-x-4 items-center'>
            {adminName && (
              <Button
                variant='secondary-outline'
                title='Skip Onboarding'
                onClick={() => {
                  window.localStorage.setItem('isSkipped', 'true');
                  participantId !== null ? navigate('/overview') : navigate('/espp');
                }}
              />
            )}
            {!isMobile && (
              <Button
                variant='error'
                title='Logout'
                onClick={() => {
                  authService
                    .logout({ force: true })
                    .then(() => {})
                    .catch(() => {});
                }}
              />
            )}
          </div>
        </NavbarContainerMain>
      )}
      {token &&
        id &&
        (isPptView ||
          esppParticipantId ||
          signUpInfo?.taxCertificationFilled ||
          !onboardingStatus?.requiresPartB) && (
          <NavbarContainerMain hasNotify={showNotify} isAdminView={isPptView} className='nav'>
            <div className='hidden md:block'>
              <Link to={skipped === 'true' ? '/overview' : '/'}>
                {/* Only show logo after branding config has resolved */}
                {isBrandingResolved &&
                  (appLogoUrl ? (
                    <img
                      src={appLogoUrl}
                      alt='Logo'
                      className='nav-brand'
                      style={{ height: '40px', objectFit: 'contain' }}
                    />
                  ) : (
                    <Logo className='nav-brand' />
                  ))}
              </Link>
            </div>
            <div className='block md:hidden'>
              {/* Only show logo after branding config has resolved */}
              {isBrandingResolved &&
                (appLogoUrl ? (
                  <img
                    src={appLogoUrl}
                    alt='Logo'
                    className='nav-brand'
                    style={{ height: '40px', objectFit: 'contain' }}
                  />
                ) : (
                  <Logo className='nav-brand' />
                ))}
            </div>
            {participantId !== null &&
              (skipped === 'true' ||
                !onboardingStatus?.requiresPartB ||
                (isPptView && isPptView)) && (
                <>
                  {isAuth && token.length > 0 && (
                    <>
                      <div className={clsx('items-center nav-mobile', isMobile && 'hidden')}>
                        <Notification
                          NotificationCount={NotificationCount}
                          isModalOpen={isModalOpen}
                          openModal={() => {
                            openModal();
                          }}
                        />
                        <Button
                          title='Menu'
                          variant='secondary'
                          icon='menu'
                          onClick={() => {
                            onSideNavClick(false);
                          }}
                          btnClassName='nav-menu'
                        />
                        <>
                          <MobileNavOverlay
                            className={openSideNav ? 'open' : ''}
                            onClick={() => onSideNavClick(true)}
                          />
                          <NavMenuMobileContainer className={openSideNav ? 'open' : ''}>
                            <MobileNavItem className='static navMenuItem'>
                              <div className='nav-close-top-right'>
                                <Button
                                  title=''
                                  variant='primary-dark'
                                  icon='close'
                                  onClick={() => {
                                    onSideNavClick(true);
                                  }}
                                  btnClassName='nav-mobile--close'
                                />
                              </div>
                              <Heading variant='h6' title='Menu' className='nav-mobile--heading' />
                              <div className='nav-items'>
                                <NavLink
                                  to='/overview'
                                  className={({ isActive }: { isActive: boolean }) =>
                                    isActive ? 'active p-2' : ''
                                  }
                                  onClick={() => {
                                    onSideNavClick(true);
                                  }}>
                                  <Heading variant='body' title='Overview' />
                                </NavLink>
                                <NavLink
                                  to='/holdings'
                                  className={({ isActive }: { isActive: boolean }) =>
                                    isActive ? 'active p-2' : ''
                                  }
                                  onClick={() => {
                                    onSideNavClick(true);
                                  }}>
                                  <Heading variant='body' title='My Portfolio' />
                                </NavLink>
                              </div>
                            </MobileNavItem>
                            <MobileNavItem className='static navMenuItem'>
                              <Heading
                                variant='h6'
                                title={userAuthList?.currentUser?.name}
                                className='nav-mobile--heading'
                              />
                              <div className='nav-items'>
                                <NavLink
                                  to='/profile'
                                  className={({ isActive }) => (isActive ? 'active p-2' : '')}
                                  onClick={() => {
                                    onSideNavClick(true);
                                  }}>
                                  <Heading variant='body' title='My Profile' />
                                </NavLink>
                                <NavLink
                                  to='/settings'
                                  className={({ isActive }: { isActive: boolean }) =>
                                    isActive ? 'active p-2' : ''
                                  }
                                  onClick={() => {
                                    onSideNavClick(true);
                                  }}>
                                  <Heading variant='body' title='Security' />
                                </NavLink>
                                <NavLink
                                  to='/notification'
                                  className={({ isActive }: { isActive: boolean }) =>
                                    isActive ? 'active p-2' : ''
                                  }
                                  onClick={() => {
                                    onSideNavClick(true);
                                  }}>
                                  <Heading variant='body' title='Notification' />
                                </NavLink>
                                <NavLink
                                  to='/financial-institution'
                                  className={({ isActive }: { isActive: boolean }) =>
                                    isActive ? 'active p-2' : ''
                                  }
                                  onClick={() => {
                                    onSideNavClick(true);
                                  }}>
                                  <Heading variant='body' title='Financial Institution' />
                                </NavLink>
                                <NavLink
                                  to='/e-delivery'
                                  className={({ isActive }: { isActive: boolean }) =>
                                    isActive ? 'active p-2' : ''
                                  }
                                  onClick={() => {
                                    onSideNavClick(true);
                                  }}>
                                  <Heading variant='body' title='E-Delivery' />
                                </NavLink>
                                <Heading
                                  variant='body'
                                  title='Log Out'
                                  onClick={async () => {
                                    await authService.logout({ force: true });
                                  }}
                                  className='cursor-pointer'
                                />
                                {/* <div className='nav-close'>
                                <Button
                                  title='Close'
                                  variant='primary-dark'
                                  icon='close'
                                  onClick={() => {
                                    onSideNavClick(true);
                                  }}
                                  btnClassName='nav-mobile--close'
                                />
                              </div> */}
                              </div>
                            </MobileNavItem>
                          </NavMenuMobileContainer>
                        </>
                      </div>
                      <NavMenuContainer className='relative nav-main'>
                        <NavMenuCon className='static navMenuItem'>
                          <NavLink
                            to='/overview'
                            className={({ isActive }: { isActive: boolean }) =>
                              isActive ? 'active' : ''
                            }>
                            Overview
                          </NavLink>
                        </NavMenuCon>
                        <NavMenuCon className='static navMenuItem'>
                          <NavLink
                            to='/holdings'
                            className={({ isActive }: { isActive: boolean }) =>
                              isActive ? 'active' : ''
                            }>
                            My Portfolio
                          </NavLink>
                        </NavMenuCon>

                        <div
                          className='relative flex items-center gap-x-2'
                          onClick={() => {
                            setToggleTransactions(!toggleTransactions);
                          }}>
                          <NavMenuCon className='static navMenuItem'>
                            <NavLink
                              to='/transactions'
                              className={({ isActive }) =>
                                isActive ? 'active pointer-events-none' : 'pointer-events-none'
                              }>
                              Transactions
                            </NavLink>
                          </NavMenuCon>
                          {toggleTransactions && (
                            <ProfileDropdownMenu ref={wrapperRef} className='nav-subMenu'>
                              {TransactionsList.map((item: { url: string; name: string }) => (
                                <DropDownMenuAdmin>
                                  <NavLink
                                    to={item.url}
                                    onClick={() => {
                                      setToggleTransactions(!toggleTransactions);
                                    }}>
                                    <h1 className='para' role='button'>
                                      {item.name}
                                    </h1>
                                  </NavLink>
                                </DropDownMenuAdmin>
                              ))}
                            </ProfileDropdownMenu>
                          )}
                        </div>
                        {/* {esppParticipantId !== null && (
                        <NavMenuCon className='static navMenuItem'>
                          <NavLink
                            to='/espp'
                            className={({ isActive }: any) => isActive && 'active'}>
                            ESPP Award
                          </NavLink>
                        </NavMenuCon>
                      )} */}
                        <Notification
                          NotificationCount={NotificationCount}
                          isModalOpen={isModalOpen}
                          openModal={() => {
                            openModal();
                          }}
                        />
                        {/* <AnimationWrapper>
                        <div className='relative'>
                          <ChatDrawer
                            closeModal={closeChatModal}
                            isChatModalOpen={isChatModalOpen}
                          />
                        </div>
                      </AnimationWrapper> */}
                      </NavMenuContainer>
                      {/* <Chatbot
                        headers={{
                          url:
                            process.env.REACT_APP_WEALTHLANE_API_ENDPOINT! +
                            '/api/customer/api/ai/chat',
                          customerName: userAuthList?.currentUser?.name,
                          source: 'participant',
                        }}
                        axiosInstance={axiosInstance}
                      /> */}

                      <ProfileDropdownContainer className='nav-main'>
                        {isPershingEligible && (
                          <Button
                            variant='secondary-outline'
                            title='View Documents'
                            onClick={() => {
                              handlePershingDocument(participantId);
                            }}
                          />
                        )}
                        <Spacer width={5} />
                        {!currentTenantInfoLoading && (
                          <NavLogo data={currentTenantInfo} handleRequest={customerHandleRequest} />
                        )}
                        <Spacer width={5} />
                        <ProfileDropWrapper>
                          <ProfileDrop
                            onClick={() => {
                              setOpen(!open);
                            }}>
                            <div className='font-semibold title' id='profileUserName'>
                              <div className='font-semibold'>
                                {signUpInfo?.firstName != null
                                  ? `${signUpInfo?.firstName} ${signUpInfo?.lastName ?? ''}`
                                  : '-'}
                              </div>
                              {adminName && <div className='text-sm'>{adminName}</div>}
                            </div>
                            <span>
                              <span className='material-symbols-rounded downIcon'>
                                arrow_drop_down
                              </span>
                            </span>
                          </ProfileDrop>
                          {open && (
                            <ProfileDropdownMenu ref={wrapperRef}>
                              <Link
                                to='/profile'
                                onClick={() => {
                                  setOpen(false);
                                }}>
                                <DropDownMenuSuperAdmin>My Profile</DropDownMenuSuperAdmin>
                              </Link>
                              <Link
                                to='/settings'
                                onClick={() => {
                                  setOpen(false);
                                }}>
                                <DropDownMenuSuperAdmin>Security</DropDownMenuSuperAdmin>
                              </Link>
                              {onboardingStatus?.requiresPartB && (
                                <Link
                                  to='/onboarding'
                                  onClick={() => {
                                    setOpen(false);
                                    localStorage.setItem('isSkipped', 'false');
                                  }}>
                                  <DropDownMenuSuperAdmin>Onboarding</DropDownMenuSuperAdmin>
                                </Link>
                              )}
                              {onboardingStatus?.requiresPartB && (
                                <Link
                                  to='/onboarded-details'
                                  onClick={() => {
                                    setOpen(false);
                                  }}>
                                  <DropDownMenuSuperAdmin>Onboarded Details</DropDownMenuSuperAdmin>
                                </Link>
                              )}
                              <Link
                                to='/notification'
                                onClick={() => {
                                  setOpen(false);
                                }}>
                                <DropDownMenuSuperAdmin>Notification</DropDownMenuSuperAdmin>
                              </Link>
                              <Link
                                to='/financial-institution'
                                onClick={() => {
                                  setOpen(false);
                                }}>
                                <DropDownMenuSuperAdmin>
                                  Financial Institution
                                </DropDownMenuSuperAdmin>
                              </Link>
                              {participantTradePreClearances?.isTradePreClearanceRequestAllowed && (
                                <Link
                                  to='/pre-clearance-request'
                                  onClick={() => {
                                    setOpen(false);
                                  }}>
                                  <DropDownMenuSuperAdmin>
                                    Pre-Clearance Request
                                  </DropDownMenuSuperAdmin>
                                </Link>
                              )}
                              {!isMobile && (
                                <DropDownMenuSuperAdmin
                                  className='logoutBtn'
                                  onClick={() => {
                                    authService
                                      .logout({ force: true })
                                      .then(() => {})
                                      .catch(() => {});
                                  }}
                                  id='logoutButton'>
                                  <h1 className='para' role='button'>
                                    Logout
                                  </h1>
                                </DropDownMenuSuperAdmin>
                              )}
                            </ProfileDropdownMenu>
                          )}
                        </ProfileDropWrapper>
                      </ProfileDropdownContainer>
                    </>
                  )}
                </>
              )}

            {participantId === null && skipped !== 'false' && (
              <>
                {isAuth && token.length > 0 && (
                  <>
                    <div className={clsx('items-center nav-mobile', isMobile && 'hidden')}>
                      <Button
                        title='Menu'
                        variant='secondary'
                        icon='menu'
                        onClick={() => {
                          onSideNavClick(false);
                        }}
                        btnClassName='nav-menu'
                      />
                      <>
                        <MobileNavOverlay
                          className={openSideNav ? 'open' : ''}
                          onClick={() => onSideNavClick(true)}
                        />
                        <NavMenuMobileContainer className={openSideNav ? 'open' : ''}>
                          <MobileNavItem className='static navMenuItem'>
                            <div className='nav-close-top-right'>
                              <Button
                                title=''
                                variant='primary-dark'
                                icon='close'
                                onClick={() => {
                                  onSideNavClick(true);
                                }}
                                btnClassName='nav-mobile--close'
                              />
                            </div>
                            <Heading variant='h6' title='Menu' className='nav-mobile--heading' />
                            <div className='nav-items'>
                              <NavLink
                                to='/holdings'
                                className={({ isActive }: { isActive: boolean }) =>
                                  isActive ? 'active p-2' : ''
                                }
                                onClick={() => {
                                  onSideNavClick(true);
                                }}>
                                <Heading variant='body' title='My Portfolio' />
                              </NavLink>
                            </div>
                          </MobileNavItem>
                          <MobileNavItem className='static navMenuItem'>
                            <Heading
                              variant='h6'
                              title={userAuthList?.currentUser?.name}
                              className='nav-mobile--heading'
                            />
                            <div className='nav-items'>
                              <NavLink
                                to='/profile'
                                className={({ isActive }) => (isActive ? 'active p-2' : '')}
                                onClick={() => {
                                  onSideNavClick(true);
                                }}>
                                <Heading variant='body' title='My Profile' />
                              </NavLink>
                              <Heading
                                variant='body'
                                title='Log Out'
                                onClick={async () => {
                                  await authService.logout({ force: true });
                                }}
                                className='cursor-pointer'
                              />
                            </div>
                          </MobileNavItem>
                        </NavMenuMobileContainer>
                      </>
                    </div>
                    <NavMenuContainer className='relative nav-main'>
                      <NavMenuCon className='static navMenuItem'>
                        <NavLink
                          to='/holdings'
                          className={({ isActive }: { isActive: boolean }) =>
                            isActive ? 'active' : ''
                          }>
                          My Portfolio
                        </NavLink>
                      </NavMenuCon>
                    </NavMenuContainer>
                  </>
                )}
                <ProfileDropdownContainer className='nav-main'>
                  <ProfileDropWrapper>
                    <ProfileDrop
                      onClick={() => {
                        setOpen(!open);
                      }}>
                      <div className='font-semibold title' id='profileUserName'>
                        {userAuthList ? userAuthList?.currentUser?.name : '-'}
                      </div>
                      <span>
                        <span className='material-symbols-rounded downIcon'>arrow_drop_down</span>
                      </span>
                    </ProfileDrop>
                    {open && (
                      <ProfileDropdownMenu ref={wrapperRef}>
                        {participantId !== null && onboardingStatus?.requiresPartB === true && (
                          <Link
                            to='/onboarding'
                            onClick={() => {
                              setOpen(false);
                              localStorage.setItem('isSkipped', 'false');
                            }}>
                            <DropDownMenuSuperAdmin>Onboarding</DropDownMenuSuperAdmin>
                          </Link>
                        )}
                        {/* <Link
                          to='/profile'
                          onClick={() => {
                            setOpen(false);
                          }}>
                          <DropDownMenuSuperAdmin>My Profile</DropDownMenuSuperAdmin>
                        </Link> */}
                        {!isMobile && (
                          <DropDownMenuSuperAdmin
                            className='logoutBtn'
                            onClick={() => {
                              authService
                                .logout({ force: true })
                                .then(() => {})
                                .catch(() => {});
                            }}
                            id='logoutButton'>
                            <h1 className='para' role='button'>
                              Logout
                            </h1>
                          </DropDownMenuSuperAdmin>
                        )}
                      </ProfileDropdownMenu>
                    )}
                  </ProfileDropWrapper>
                </ProfileDropdownContainer>
              </>
            )}

            {/* {skipped === 'true' && participantId === null && !onboarding && (
            <>
              <NavMenuContainer className='relative nav-main'>
                <NavMenuCon className='static navMenuItem'>
                  <NavLink to='/espp' className={({ isActive }: any) => isActive && 'active'}>
                    ESPP Award
                  </NavLink>
                </NavMenuCon>
              </NavMenuContainer>
            </>
          )} */}

            {/* {!onboarding && skipped === 'true' && participantId === null && (
            <div className='flex items-center justify-center'>
              <div className='logout'>
                <Button
                  variant='primary'
                  title='Onboarding'
                  onClick={() => {
                    window.localStorage.setItem('isSkipped', 'false');
                    navigate('/onboarding');
                  }}></Button>
              </div>
              <ProfileDropdownContainer className='nav-main'>
                <ProfileDropWrapper>
                  <ProfileDrop
                    onClick={() => {
                      setOpen(!open);
                    }}>
                    <div className='font-semibold title' id='profileUserName'>
                      {userAuthList ? userAuthList?.currentUser?.name : '-'}
                    </div>
                    <span>
                      <span className='material-symbols-rounded downIcon'>arrow_drop_down</span>
                    </span>
                  </ProfileDrop>
                  {open && (
                    <ProfileDropdownMenu ref={wrapperRef}>
                      <DropDownMenuSuperAdmin
                        className='logoutBtn'
                        onClick={() => {
                          // authService.logout();
                          authService
                            .logout()
                            .then(() => {})
                            .catch(() => {});
                        }}>
                        <div className='para'>Logout</div>
                      </DropDownMenuSuperAdmin>
                    </ProfileDropdownMenu>
                  )}
                </ProfileDropWrapper>
              </ProfileDropdownContainer>
            </div>
          )} */}

            {onboardingStatus?.requiresPartB &&
              !isPptView &&
              ((skipped === null && participantId !== null) || skipped === 'false') && (
                <div className='flex'>
                  {/* {esppParticipantId !== null && ( */}
                  {/* <div className='logout'>
                  <Button
                    variant='primary-outline'
                    title='Skip Onboarding'
                    onClick={() => {
                      window.localStorage.setItem('isSkipped', 'true');
                      participantId !== null ? navigate('/overview') : navigate('/espp');
                    }}></Button>
                </div> */}
                  {/* )} */}
                  <div className='logout'>
                    {!isMobile && (
                      <Button
                        variant='danger'
                        title='Logout'
                        id='logoutButton'
                        onClick={() => {
                          authService
                            .logout({ force: true })
                            .then(() => {})
                            .catch(() => {});
                        }}></Button>
                    )}
                  </div>
                </div>
              )}
          </NavbarContainerMain>
        )}
      {isOffline && (
        <NetworkNotice id='networkNotice'>
          <span className='material-symbols-outlined network-notice__icon'>wifi_off</span>
          We are experiencing transient network issues. Certain features may be temporarily limited,
          but your current view and data are preserved.
        </NetworkNotice>
      )}
      <AnimationWrapper>
        {isModalOpen && (
          <NotificationModal
            handleRequest={handleRequest}
            isModalOpen={isModalOpen}
            newMessage={newMessage}
            closeModal={closeModal}
          />
        )}
      </AnimationWrapper>
      <ChatbotOverlay />
      <AnimationWrapper>
        {isPershingRedirectOpen && (
          <AlertPopup
            isShow={isPershingRedirectOpen}
            closeModal={closePershingRedirectModal}
            customTitle={'You are being redirected to a new page.'}
            // subTitle={
            //   !isPershingSuccess && pershingRedirectMessage?.error?.participants?.length > 0 ? (
            //     <div className='flex flex-col gap-2'>
            //       <div className='grid grid-cols-3 gap-2'>
            //         <div className='col-span-1'>
            //           <Heading variant='body' title='Participant Name' />
            //         </div>
            //         <div className='col-span-2'>
            //           <Heading variant='body' title='Reason' />
            //         </div>
            //       </div>
            //       {pershingRedirectMessage.error.participants.map((item: any, index: number) => {
            //         return (
            //           <div className='grid grid-cols-3 gap-8 mt-2'>
            //             <div className='col-span-1'>{item.name}</div>
            //             <div className='col-span-2'>{item.reason}</div>
            //           </div>
            //         );
            //       })}
            //     </div>
            //   ) : undefined
            // }
            ConfirmTitle={'Continue'}
            handleSubmit={() => {
              window.open(pershingRedirectUrl, '_blank');
              closePershingRedirectModal();
            }}
          />
        )}
      </AnimationWrapper>
    </>
  );
};

export default Navbar;
